import { FC, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker'
import { DateRange, LocalizationProvider } from '@mui/x-date-pickers-pro'
import {
  Button,
  Stack,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import LoadedDateTimePicker from './loaded-date-time-picker/LoadedDateTimePicker'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { RangeFilter } from '$constants'
import { useCompanySettings } from '$hooks'
import {
  lastDayInRange,
  getReportRangeForDay,
} from 'src/components/common/helpers'
import { isMobile } from '$utils'

const PERIOD_ORDER = [RangeFilter.Day, RangeFilter.Week, RangeFilter.Month]

/* Whether switching range filters keeps the range being edited. A wider period, or a custom
range, has something sensible to start from. Going narrower does not, so the user picks again. */
const keepsPickedRange = (from: RangeFilter, to: RangeFilter): boolean => {
  if (from === RangeFilter.Custom) return false
  if (to === RangeFilter.Custom) return true

  return PERIOD_ORDER.indexOf(to) > PERIOD_ORDER.indexOf(from)
}

type LoadedCalendarReportFilterProps = {
  dateRange?: DateRange<Dayjs>
  dateRangeFilter?: RangeFilter
  prevDateRangeFilter?: RangeFilter
  onApply: (
    dateRange: DateRange<Dayjs>,
    rangeFilter: RangeFilter,
    prevRangeFilter: RangeFilter
  ) => void
  onCancel: () => void
  calendars?: 1 | 2 | 3
  currentMonthCalendarPosition?: 1 | 2 | 3
  disabled?: boolean
  readOnly?: boolean
}

const LoadedCalendarReportFilter: FC<LoadedCalendarReportFilterProps> = ({
  dateRange = [dayjs(), dayjs()],
  dateRangeFilter = RangeFilter.Day,
  prevDateRangeFilter = RangeFilter.Day,
  onApply,
  onCancel,
  calendars = 3,
  currentMonthCalendarPosition = 1,
  disabled = false,
  readOnly = false,
}) => {
  const theme = useTheme<Theme>()
  const matches1600Screen = useMediaQuery('(max-width:1600px)')
  const matches1420Screen = useMediaQuery('(max-width:1420px)')
  const matches1110Screen = useMediaQuery('(max-width:1110px)')

  const { weekStartPreference, dayStartPreference, localePreference } =
    useCompanySettings()

  const activeCompanyDayStartPreference: string[] = useMemo(
    () => dayStartPreference.split(':'),
    [dayStartPreference]
  )

  const [rangeFilter, setRangeFilter] = useState<RangeFilter>(dateRangeFilter)
  const [prevRangeFilter, setPrevRangeFilter] =
    useState<RangeFilter>(prevDateRangeFilter)

  // The range being edited, exactly as it is applied: inclusive start, exclusive end
  const [startDateTime, setStartDateTime] = useState<Dayjs | null>(dateRange[0])
  const [endDateTime, setEndDateTime] = useState<Dayjs | null>(
    () => dateRange[1] ?? dayjs()
  )

  // The calendar highlights whole days, so it takes the last day covered, not the exclusive end
  const highlightedRange: DateRange<Dayjs> = [
    startDateTime,
    endDateTime ? lastDayInRange(endDateTime, dayStartPreference) : null,
  ]

  const rangeFilterButtonSx: SxProps<Theme> = {
    borderColor: theme.palette.text.primary,
    color: theme.palette.text.primary,
    '&.MuiButton-contained ': {
      backgroundColor: theme.palette.grey[300],
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
      borderColor: theme.palette.grey[300],
    },
    ...(matches1600Screen && { width: '45%' }),
  }

  const setRangeForDay = (
    day: Dayjs,
    newRangeFilter: RangeFilter,
    newPrevRangeFilter: RangeFilter
  ) => {
    const { start, end } = getReportRangeForDay(
      day,
      newRangeFilter,
      newPrevRangeFilter,
      weekStartPreference,
      dayStartPreference,
      // A custom range keeps whatever times the user set, the rest run day start to day start
      newRangeFilter === RangeFilter.Custom
        ? { start: startDateTime, end: endDateTime }
        : undefined
    )

    setStartDateTime(start)
    setEndDateTime(end)
  }

  const handleOnDaySelect = (day: Dayjs) =>
    setRangeForDay(day, rangeFilter, prevRangeFilter)

  const handleRangeFilterClick = (newRangeFilter: RangeFilter) => {
    if (newRangeFilter === rangeFilter) return

    setPrevRangeFilter(rangeFilter)
    setRangeFilter(newRangeFilter)

    if (startDateTime && keepsPickedRange(rangeFilter, newRangeFilter)) {
      setRangeForDay(startDateTime, newRangeFilter, rangeFilter)
    } else {
      setStartDateTime(null)
      setEndDateTime(null)
    }
  }

  const handleOnChange = (dateRange: DateRange<dayjs.Dayjs>) => {
    if (rangeFilter === RangeFilter.Custom && dateRange) {
      if (dateRange[0]) {
        const startDate = dateRange[0]
          .set('hour', +activeCompanyDayStartPreference[0])
          .set('minute', +activeCompanyDayStartPreference[1])
          .set('second', +activeCompanyDayStartPreference[2])
        setStartDateTime(startDate)
      }
      if (dateRange[1]) {
        const endDate = dateRange[1]
          .set('hour', +activeCompanyDayStartPreference[0])
          .set('minute', +activeCompanyDayStartPreference[1])
          .set('second', +activeCompanyDayStartPreference[2])
          .add(1, 'day')
        setEndDateTime(endDate)
      }
    }
  }

  const handleOnApply = () => {
    if (!startDateTime || !endDateTime) {
      onApply([dayjs(), dayjs().add(1, 'day')], rangeFilter, prevRangeFilter)
    } else {
      onApply([startDateTime, endDateTime], rangeFilter, prevRangeFilter)
    }
  }

  const handleOnCancel = () => onCancel()

  const handleStartTimeOnAccept = (date: Dayjs | null) => {
    if (date && endDateTime && date.isAfter(endDateTime)) {
      setEndDateTime(null)
    }
    setStartDateTime(date)
  }

  const handleEndTimeOnAccept = (date: Dayjs | null) => setEndDateTime(date)

  const startEndDateTimeLabelProps: TypographyProps = {
    color:
      rangeFilter === 'custom'
        ? theme.palette.text.primary
        : theme.palette.text.disabled,
    sx: {
      minWidth: '40px',
    },
  }

  const stackDirection = isMobile() ? 'column' : 'row'

  return (
    <Stack
      direction={stackDirection}
      sx={{
        justifyContent: matches1600Screen ? 'space-around' : 'flex-end',
        displayPrint: 'none',
        ...(isMobile() && {
          borderBottom: '1px solid #ccc',
          paddingBottom: '16px',
          marginBottom: '16px',
          alignItems: 'end',
        }),
      }}
    >
      <Stack
        sx={{
          ...(isMobile() && {
            marginRight: '16px !important',
          }),
        }}
      >
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={localePreference.toLocaleLowerCase()}
        >
          <StaticDateRangePicker
            slots={{
              toolbar: () => null,
            }}
            slotProps={{
              actionBar: { actions: [] },
              day: {
                ...(rangeFilter !== RangeFilter.Custom && {
                  onDaySelect: (param) => handleOnDaySelect(param),
                }),
              },
            }}
            onChange={handleOnChange}
            calendars={
              matches1110Screen
                ? 1
                : matches1420Screen
                ? calendars < 2
                  ? calendars
                  : 2
                : calendars
            }
            currentMonthCalendarPosition={currentMonthCalendarPosition}
            disableHighlightToday={false}
            value={highlightedRange}
            disabled={disabled}
            readOnly={readOnly}
            reduceAnimations={true}
            sx={{
              backgroundColor: 'transparent !important',
              '.MuiPickersArrowSwitcher-root': {
                paddingTop: 0,
              },
            }}
          />
        </LocalizationProvider>
      </Stack>
      <Stack
        sx={{
          justifyContent: 'space-between',
          ...(matches1600Screen && {
            marginLeft: '0px !important',
          }),
          ...(isMobile() && {
            width: 'calc(100% - 22px)',
          }),
        }}
      >
        <Stack>
          <Stack direction={matches1600Screen ? 'column' : 'row'}>
            <Stack direction="row">
              <Button
                variant={rangeFilter === 'day' ? 'contained' : 'outlined'}
                onClick={() => handleRangeFilterClick(RangeFilter.Day)}
                sx={{
                  marginRight: '2rem',
                  ...rangeFilterButtonSx,
                }}
                disableRipple
              >
                Day
              </Button>
              <Button
                variant={rangeFilter === 'week' ? 'contained' : 'outlined'}
                onClick={() => handleRangeFilterClick(RangeFilter.Week)}
                sx={{
                  marginRight: '2rem',
                  ...rangeFilterButtonSx,
                }}
                disableRipple
              >
                Week
              </Button>
            </Stack>
            <Stack direction="row">
              <Button
                variant={rangeFilter === 'month' ? 'contained' : 'outlined'}
                onClick={() => handleRangeFilterClick(RangeFilter.Month)}
                sx={{
                  marginRight: '2rem',
                  ...rangeFilterButtonSx,
                }}
                disableRipple
              >
                Month
              </Button>
              <Button
                variant={rangeFilter === 'custom' ? 'contained' : 'outlined'}
                onClick={() => handleRangeFilterClick(RangeFilter.Custom)}
                sx={{ ...rangeFilterButtonSx }}
                disableRipple
              >
                Custom
              </Button>
            </Stack>
          </Stack>
          <Stack
            direction={'column'}
            sx={{
              ...(isMobile() && {
                width: 'calc(100% - 28px)',
              }),
            }}
          >
            {matches1600Screen && (
              <Typography {...startEndDateTimeLabelProps}>Start</Typography>
            )}
            <LoadedDateTimePicker
              name="startDateTime"
              value={startDateTime}
              {...(!matches1600Screen && {
                label: 'Start',
              })}
              fullWidth={true}
              disabled={rangeFilter === 'custom' ? false : true}
              onAccept={handleStartTimeOnAccept}
              onChange={handleStartTimeOnAccept}
              sx={{
                ...(matches1600Screen && {
                  minWidth: '240px !important',
                  '& .MuiInputBase-input': {
                    paddingLeft: '5px !important',
                    paddingRight: '5px !important',
                  },
                }),
              }}
              {...(matches1600Screen && {
                containerProps: {
                  sx: {
                    marginTop: '0px !important',
                  },
                },
              })}
            />

            {matches1600Screen && (
              <Typography {...startEndDateTimeLabelProps}>End</Typography>
            )}
            <LoadedDateTimePicker
              name="endDateTime"
              value={endDateTime}
              {...(!matches1600Screen && { label: 'End' })}
              disabled={rangeFilter === 'custom' ? false : true}
              minDateTime={startDateTime}
              onAccept={handleEndTimeOnAccept}
              fullWidth={true}
              onChange={handleEndTimeOnAccept}
              sx={{
                ...(matches1600Screen && {
                  minWidth: '240px !important',
                  '& .MuiInputBase-input': {
                    paddingLeft: '5px !important',
                    paddingRight: '5px !important',
                  },
                }),
              }}
              {...(matches1600Screen && {
                containerProps: {
                  sx: {
                    marginTop: '0px !important',
                  },
                },
              })}
            />
          </Stack>
        </Stack>
        <Stack
          direction={'row'}
          sx={{
            alignSelf: 'self-end',
            ...(matches1600Screen && {
              marginRight: '10px !important',
            }),
            ...(isMobile() && {
              paddingTop: '16px',
              paddingRight: '16px',
              paddingBottom: '8px',
            }),
          }}
        >
          <Button
            variant="contained"
            onClick={handleOnApply}
            disabled={
              !startDateTime ||
              !endDateTime ||
              startDateTime?.isAfter(endDateTime)
            }
          >
            Apply
          </Button>
          <Button variant="outlined" onClick={handleOnCancel}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default LoadedCalendarReportFilter
